/**
 * Add Checklist Item Modal
 *
 * Modal for adding custom checklist items. Backdrop must not use flex:1 in the
 * same column as the sheet — that collapses the form (inputs hidden).
 */

import { useState } from 'react';
import {
  View,
  Modal,
  ScrollView,
  Pressable,
  Platform,
  KeyboardAvoidingView,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { InputField } from '@/components/ui/InputField';
import { Button } from '@/components/ui/Button';
import { SelectField } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { Calendar } from '@/components/ui/Calendar';
import { ThemedText, SubHeading } from '@/components/ui/Text';
import type { ChecklistItemGroup, ChecklistItem } from '@/services/checklists';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';

type AddChecklistItemModalProps = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (item: {
    title: string;
    notes?: string;
    dueDate?: Date;
    category?: ChecklistItem['category'];
    group: ChecklistItemGroup;
  }) => Promise<void>;
  group: ChecklistItemGroup;
};

const HEADER_VERTICAL_PADDING = 16;
const HEADER_BORDER = 1;
const APPROX_HEADER_HEIGHT = 52 + HEADER_VERTICAL_PADDING * 2 + HEADER_BORDER;

export function AddChecklistItemModal({
  visible,
  onClose,
  onSubmit,
  group,
}: AddChecklistItemModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const sheetMaxHeight = Math.min(windowHeight * 0.92, 720);

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState<ChecklistItem['category'] | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoryOptions =
    group === 'planning'
      ? [
          { value: 'visa', label: 'Visa' },
          { value: 'health', label: 'Health' },
          { value: 'insurance', label: 'Insurance' },
          { value: 'booking', label: 'Booking' },
          { value: 'documents', label: 'Documents' },
          { value: 'preparation', label: 'Preparation' },
          { value: 'transportation', label: 'Transportation' },
          { value: 'activities', label: 'Activities' },
          { value: 'other', label: 'Other' },
        ]
      : [
          { value: 'clothing', label: 'Clothing' },
          { value: 'essentials', label: 'Essentials' },
          { value: 'other', label: 'Other' },
        ];

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmit({
        title: title.trim(),
        notes: notes.trim() || undefined,
        dueDate,
        category,
        group,
      });

      setTitle('');
      setNotes('');
      setCategory(undefined);
      setDueDate(undefined);
      setError(null);
      onClose();
    } catch (err) {
      console.error('[AddChecklistItemModal] Error submitting:', err);
      setError(humanizeAsyncError(err, 'We couldn’t add this item. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setTitle('');
    setNotes('');
    setCategory(undefined);
    setDueDate(undefined);
    setError(null);
    onClose();
  };

  const scrollMaxHeight = Math.max(220, sheetMaxHeight - APPROX_HEADER_HEIGHT);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={handleClose}
      presentationStyle="overFullScreen"
      accessibilityViewIsModal={true}
      accessibilityLabel={`Add custom ${group} item`}
    >
      <SelectFieldModalProvider>
      <View style={[styles.overlay, { backgroundColor: theme.colors.modalOverlay }]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleClose}
          accessibilityLabel="Close modal background"
          accessibilityRole="button"
        />

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? insets.top : 0}
          style={styles.keyboardWrap}
        >
          <View
            accessibilityLabel={`Add custom ${group} item form`}
            style={[
              styles.sheet,
              {
                backgroundColor: theme.colors.surface,
                borderTopLeftRadius: theme.radii.xl,
                borderTopRightRadius: theme.radii.xl,
                maxHeight: sheetMaxHeight,
              },
            ]}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingHorizontal: theme.spacing(2),
                paddingVertical: HEADER_VERTICAL_PADDING,
                borderBottomWidth: HEADER_BORDER,
                borderBottomColor: theme.colors.borderMuted,
              }}
            >
              <SubHeading>Add Custom Item</SubHeading>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Close form"
                onPress={handleClose}
                style={({ pressed }) => ({
                  padding: theme.spacing(0.5),
                  borderRadius: theme.radii.md,
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </Pressable>
            </View>

            <ScrollView
              style={{ maxHeight: scrollMaxHeight }}
              contentContainerStyle={{
                padding: theme.spacing(2),
                gap: theme.spacing(2),
                paddingBottom: theme.spacing(3) + insets.bottom,
              }}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
              automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
              showsVerticalScrollIndicator
            >
              <InputField
                label="Item Title"
                placeholder={`e.g., ${group === 'planning' ? 'Book restaurant reservations' : 'Pack sunglasses'}`}
                value={title}
                onChangeText={(value) => {
                  setTitle(value);
                  if (error) setError(null);
                }}
                errorText={error || undefined}
                required
                autoFocus
              />

              <InputField
                label="Notes (optional)"
                placeholder="Add any additional details..."
                value={notes}
                onChangeText={setNotes}
                multiline
                numberOfLines={3}
              />

              <SelectField
                label="Category (optional)"
                value={category}
                options={categoryOptions}
                onValueChange={(value) => setCategory(value as ChecklistItem['category'])}
                placeholder="Select category"
                allowClear
              />

              <View>
                <ThemedText variant="bodyStrong" style={{ marginBottom: theme.spacing(0.75) }}>
                  Due Date (optional)
                </ThemedText>
                <Pressable
                  onPress={() => setShowDatePicker(true)}
                  style={({ pressed }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderWidth: 1,
                    borderColor: theme.colors.border,
                    borderRadius: theme.radii.md,
                    paddingHorizontal: theme.spacing(2),
                    paddingVertical: theme.spacing(1.5),
                    backgroundColor: theme.colors.surface,
                    minHeight: 44,
                    opacity: pressed ? 0.7 : 1,
                  })}
                  accessibilityRole="button"
                  accessibilityLabel={dueDate ? `Due date: ${dueDate.toLocaleDateString()}` : 'Select due date'}
                >
                  <ThemedText color={dueDate ? theme.colors.text : theme.colors.textSecondary}>
                    {dueDate
                      ? dueDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'Select due date'}
                  </ThemedText>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                    {dueDate && (
                      <Pressable
                        onPress={(e) => {
                          e.stopPropagation();
                          setDueDate(undefined);
                        }}
                        style={{ padding: theme.spacing(0.5) }}
                        accessibilityRole="button"
                        accessibilityLabel="Clear due date"
                      >
                        <Ionicons name="close-circle" size={20} color={theme.colors.textSecondary} />
                      </Pressable>
                    )}
                    <Ionicons name="calendar-outline" size={20} color={theme.colors.textSecondary} />
                  </View>
                </Pressable>
              </View>

              <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(0.5) }}>
                <Button
                  label={submitting ? 'Adding...' : 'Add Item'}
                  onPress={handleSubmit}
                  disabled={submitting || !title.trim()}
                  loading={submitting}
                />
                <Button label="Cancel" variant="ghost" onPress={handleClose} disabled={submitting} />
              </View>
            </ScrollView>

            <Calendar
              visible={showDatePicker}
              onClose={() => setShowDatePicker(false)}
              onSelectDate={(date) => {
                setDueDate(date);
                setShowDatePicker(false);
              }}
              selectedDate={dueDate}
              minDate={new Date()}
              title="Select Due Date"
            />
          </View>
        </KeyboardAvoidingView>
      </View>
      </SelectFieldModalProvider>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  keyboardWrap: {
    width: '100%',
    maxWidth: '100%',
  },
  sheet: {
    width: '100%',
    overflow: 'hidden',
  },
});
